import { useAuth, roleLabel } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Mail, ShieldCheck, Building2, Moon, Sun, LogOut, BedDouble, Plus, X, Trash2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PushNotificationsCard from "@/components/PushNotificationsCard";

const MOTIVI = [
  { v: "riposo", label: "Riposo settimanale" },
  { v: "ferie", label: "Ferie" },
  { v: "malattia", label: "Malattia" },
  { v: "altro", label: "Altro" },
];

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function RestDaysSection({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ data: todayISO(0), motivo: "riposo", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rest-days");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSubmitting(true);
    try {
      await api.post("/rest-days", { user_id: userId, ...form });
      toast.success("Giorno di riposo registrato — le task di quel giorno non conteranno nelle medie");
      setModalOpen(false);
      setForm({ data: todayISO(0), motivo: "riposo", note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo giorno di riposo? Le task del giorno torneranno al loro stato originario.")) return;
    try {
      await api.delete(`/rest-days/${id}`);
      toast.success("Giorno di riposo rimosso");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const today = todayISO(0);
  const minDate = todayISO(-7);
  const maxDate = todayISO(7);

  return (
    <div className="ih-glass rounded-2xl p-6" data-testid="rest-days-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg tracking-tight flex items-center gap-2">
          <BedDouble className="h-4 w-4 text-lavender-500" /> I miei giorni di riposo
        </h2>
        <Button data-testid="add-rest-day-btn" onClick={() => setModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Aggiungi
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Registra riposi, ferie o malattia (entro ±7 giorni da oggi). Le task del giorno indicato non conteranno nelle tue KPI.</p>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">Nessun giorno di riposo registrato negli ultimi 60 giorni.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => {
            const removable = r.data >= minDate;
            return (
              <li key={r.id} className="flex items-center justify-between rounded-xl border border-border/60 px-4 py-2.5 text-sm" data-testid={`rest-day-${r.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium tabular-nums">{new Date(r.data).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-accent capitalize">{r.motivo}</span>
                  {r.note && <span className="text-xs text-muted-foreground">· {r.note}</span>}
                  {r.data === today && <span className="text-[10px] px-1.5 py-0.5 rounded bg-lavender-100 text-lavender-700 dark:bg-lavender-900/40 dark:text-lavender-300 uppercase">Oggi</span>}
                </div>
                {removable ? (
                  <button onClick={() => remove(r.id)} className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 h-8 w-8 rounded-md flex items-center justify-center" data-testid={`rest-day-remove-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : <span className="text-[10px] text-muted-foreground">archiviato</span>}
              </li>
            );
          })}
        </ul>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-md bg-background rounded-2xl border border-border p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rest-day-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nuovo riposo</div>
                <h3 className="font-display text-xl tracking-tight">Registra giorno</h3>
              </div>
              <button onClick={() => setModalOpen(false)} className="h-8 w-8 rounded-md hover:bg-accent flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Data (±7 giorni da oggi)</label>
                <input data-testid="rest-day-date" type="date" min={minDate} max={maxDate} value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Motivo</label>
                <select data-testid="rest-day-motivo" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                  {MOTIVI.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Note (opzionale)</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="Es. visita medica…" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Annulla</Button>
              <Button onClick={create} disabled={submitting} data-testid="rest-day-confirm">{submitting ? "Salvataggio…" : "Conferma"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Profilo() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  if (!user) return null;

  return (
    <div className="space-y-6 ih-anim-in" data-testid="profilo-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Profilo</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">
          {user.nome} <span className="font-medium">{user.cognome}</span>
        </h1>
      </div>

      <div className="ih-glass rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-lavender-300 to-lavender-500 text-white flex items-center justify-center text-xl font-semibold shadow-lg shadow-lavender-300/40">
            {(user.nome?.[0] || "") + (user.cognome?.[0] || "")}
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Ruolo</div>
            <div className="font-medium flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-lavender-500" /> {roleLabel(user.role)}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border/40">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Email</div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {user.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Hotel assegnati</div>
            <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /> {user.hotel_ids?.length || 0}</div>
          </div>
        </div>
      </div>

      {["resident_manager", "front_office_manager", "housekeeping_manager"].includes(user.role) && <RestDaysSection userId={user.id} />}

      <PushNotificationsCard />

      <div className="ih-glass rounded-2xl p-6">
        <h2 className="font-display text-lg tracking-tight mb-4">Preferenze</h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium flex items-center gap-2">
              {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              Tema {theme === "dark" ? "scuro" : "chiaro"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Cambia l'aspetto dell'interfaccia.</div>
          </div>
          <Switch data-testid="profilo-theme-switch" checked={theme === "dark"} onCheckedChange={toggle} />
        </div>
      </div>

      <Button variant="outline" data-testid="profilo-logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="gap-2">
        <LogOut className="h-4 w-4" /> Logout
      </Button>
    </div>
  );
}
